import { useDispatch, useSelector } from "react-redux";
import * as Yup from "yup";
import {
  createProduct,
  updateProduct,
  deleteProduct,
} from "reduxStore/actions/productActions";
import { useEffect, useState, useMemo } from "react";
import { debounce } from "lodash";
import { fetchOrganisations } from "reduxStore/actions/organisationActions";
import { checkRoles } from "utils/permissions";
import { searchEmissionRecords } from "reduxStore/actions/emissionRecordActions";
import { createFilterOptions } from "@mui/material/Autocomplete";

// 1 = highest priority, larger = lower
const regionPriority = {
  UK: 1,
  "Ireland - Processing Location": 2,
  Europe: 3,
  "UK - Processing Location": 4,
  "Region of Europe - Processing Location": 5,
};

export const useProductForm = ({ editMode, setOpen, currentProduct }) => {
  const dispatch = useDispatch();

  const user = useSelector((state) => state.user || {});
  const organisationId = user?.organisation?._id;
  const organisationName = user?.organisation?.name || "Your Organisation";

  const { isSystemAdmin } = checkRoles(user);

  const [ingredientSearchTerm, setIngredientSearchTerm] = useState("");
  const ingredientSearchResults = useSelector(
    (state) => state.emissionRecordSearchResults || []
  );

  const organisations = useSelector((state) => state.organisations.data);

  const [deleteProductDialogOpen, setDeleteProductDialogOpen] = useState(false);

  const [searchOrganisationId, setSearchOrganisationId] = useState(() => {
    // default to currently selected product org if present, else user’s org
    return currentProduct?.organisation?._id || organisationId || "";
  });

  // memoize a sorted copy so we don’t re-sort on every render
  const sortedIngredientSearchResults = useMemo(() => {
    return [...ingredientSearchResults].sort((a, b) => {
      const pa = regionPriority[a.applicable_region] ?? 999;
      const pb = regionPriority[b.applicable_region] ?? 999;
      return pa - pb;
    });
  }, [ingredientSearchResults]);

  const filter = createFilterOptions({
    // search across name, origin & applicable
    stringify: (opt) =>
      `${opt.source_product_name} ${opt.origin_region} ${opt.applicable_region}`,
    matchFrom: "any",
    trim: true,
  });

  // NEW: which org are we prioritising (selected org if admin picked one)
  const activeOrgId = useMemo(
    () => searchOrganisationId || organisationId || "",
    [searchOrganisationId, organisationId]
  );

  // NEW: label for that org’s group header
  const activeOrganisationName = useMemo(() => {
    const found = (organisations || []).find((o) => o._id === activeOrgId);
    return (
      found?.name ||
      (activeOrgId === organisationId
        ? organisationName
        : "Selected organisation")
    );
  }, [organisations, activeOrgId, organisationName, organisationId]);

  // memoize the fully‐prepared list so we only do it once per searchResults change
  const preparedOptions = useMemo(() => {
    const activeIdStr = String(activeOrgId || "");
    return sortedIngredientSearchResults.slice().sort((a, b) => {
      const aOrg = String(a.organisation) === activeIdStr;
      const bOrg = String(b.organisation) === activeIdStr;
      if (aOrg !== bOrg) return bOrg - aOrg; // active org first

      const pa = regionPriority[a.applicable_region] ?? 999;
      const pb = regionPriority[b.applicable_region] ?? 999;
      if (pa !== pb) return pa - pb;

      if (a.applicable_region !== b.applicable_region) {
        return (a.applicable_region || "").localeCompare(
          b.applicable_region || ""
        );
      }

      return (a.source_product_name || "").localeCompare(
        b.source_product_name || ""
      );
    });
  }, [sortedIngredientSearchResults, activeOrgId]);

  const initialIngredients = useMemo(
    () =>
      currentProduct?.ingredients?.map((ingredient) => ({
        ...ingredient,
        ingredientData:
          typeof ingredient.emissionRecordId === "object" &&
          ingredient.emissionRecordId !== null
            ? ingredient.emissionRecordId
            : null,
        emissionRecordId:
          typeof ingredient.emissionRecordId === "object" &&
          ingredient.emissionRecordId !== null
            ? ingredient.emissionRecordId._id
            : ingredient.emissionRecordId,
      })) || [],
    [currentProduct]
  );

  // inside useProductForm:
  const initialValues = useMemo(
    () => ({
      name: currentProduct?.name || "",
      organisation: currentProduct?.organisation?._id || "",
      weight: currentProduct?.weight || "",
      ingredients: initialIngredients,
    }),
    [currentProduct, initialIngredients]
  );

  const validateSchema = Yup.object().shape({
    name: Yup.string().required("Name is required"),
    organisation: Yup.string().required("Organisation is required"),
    weight: Yup.number()
      .required("Weight is required")
      .min(0, "Invalid weight"),
    ingredients: Yup.array()
      .of(
        Yup.object().shape({
          emissionRecordId: Yup.string().required(
            "Emission Record is required"
          ),
          percent: Yup.number()
            .required("Percentage is required")
            .min(0, "Invalid percentage"),
        })
      )

      .test(
        "total-percentage",
        "Total percentage must be between 90% and 100%",
        (ingredients = []) => {
          const total = ingredients.reduce(
            (sum, ingredient) => sum + (Number(ingredient.percent) || 0),
            0
          );
          return total >= 90 && total <= 100;
        }
      ),
  });

  const handleSubmit = (values) => {
    if (editMode) {
      dispatch(
        updateProduct({
          productId: currentProduct.id,
          values: values,
          organisation: isSystemAdmin ? undefined : organisationId,
        })
      );
    } else {
      dispatch(
        createProduct({
          values,
          organisation: isSystemAdmin ? undefined : organisationId,
        })
      );
    }
    setOpen(false);
  };

  const handleDelete = () => {
    dispatch(
      deleteProduct({
        productId: currentProduct.id,
        organisation: organisationId,
      })
    );
    console.log("🚀 ~ handleDelete ~ currentProduct.id:", currentProduct.id);
    console.log("🚀 ~ handleDelete ~ organisationId:", organisationId);

    setDeleteProductDialogOpen(false);
    setOpen(false);
  };

  const handleDeleteClick = () => {
    setDeleteProductDialogOpen(true);
  };

  const handleCloseDeleteDialog = () => {
    setDeleteProductDialogOpen(false);
  };

  useEffect(() => {
    const debounceFetch = debounce(() => {
      if (ingredientSearchTerm) {
        dispatch(
          searchEmissionRecords(ingredientSearchTerm, searchOrganisationId)
        );
      }
    }, 300);
    debounceFetch();
    return () => debounceFetch.cancel();
  }, [dispatch, ingredientSearchTerm, searchOrganisationId]);

  useEffect(() => {
    dispatch(fetchOrganisations());
  }, [dispatch]);

  return {
    initialValues,
    validateSchema,
    handleSubmit,
    handleDelete,
    handleDeleteClick,
    handleCloseDeleteDialog,
    deleteProductDialogOpen,
    setIngredientSearchTerm,
    ingredientSearchResults: sortedIngredientSearchResults,
    organisations,
    preparedOptions,
    filter,
    setSearchOrganisationId,
    activeOrgId,
    activeOrganisationName,
  };
};
